'use client';

import { FC, useEffect, useState } from 'react';

import { Region } from '@abg/typings';
import { isSkipPersonalisation } from '@abg/web-core/common/utils/skip-third-party';

import { isConsentGroupAllowed } from '../../shared/utils';
import { ConsentGroup } from '../../shared/utils/consent';

import { AdobeTargetScript } from './AdobeTargetScript';

type AdobeTargetScriptWrapperPropsType = {
  region: Region;
};

const consentGroupMap: Record<Region, ConsentGroup> = {
  emea: ConsentGroup.Performance,
  nam: ConsentGroup.Required,
  apac: ConsentGroup.Required,
};

export const AdobeTargetScriptWrapper: FC<
  AdobeTargetScriptWrapperPropsType
> = ({ region }) => {
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    // Perf-testing flag: personalisation off — no alloy, no consent
    // subscription. See the `skip-third-party` util.
    if (isSkipPersonalisation()) return;

    // When user accepts cookies after initial load
    const handler = () => {
      const consent = isConsentGroupAllowed(consentGroupMap[region]);
      setHasConsent(consent);
    };

    handler(); // <-- Initial check

    // OneTrust callback on consent update
    if (
      globalThis.window.OneTrust &&
      globalThis.window.OneTrust.OnConsentChanged
    ) {
      globalThis.window.OneTrust.OnConsentChanged(handler);
    } else {
      // Fallback if script not ready yet — poll until available
      const interval = setInterval(() => {
        if (globalThis.window.OneTrust?.OnConsentChanged) {
          globalThis.window.OneTrust.OnConsentChanged(handler);
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
    return;
  }, [region]);

  if (!hasConsent) return null;

  return <AdobeTargetScript />;
};
